import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { ProfileSettings } from './components/profile-settings';
import { TenantSettings } from './components/tenant-settings';
import { TeamManagement } from './components/team-management';
import { User, Building, Users } from 'lucide-react';

type TabId = 'profile' | 'tenant' | 'team';

export default function SettingsPage() {
  const user = useAuthStore((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const activeTab = (searchParams.get('tab') as TabId) || 'profile';

  const isOwnerOrAdmin = React.useMemo(() => {
    return user?.roleCode === 'OWNER' || user?.roleCode === 'ADMIN';
  }, [user]);

  const tabs = React.useMemo(() => {
    const list = [
      { id: 'profile' as TabId, label: 'My Profile', icon: User },
    ];
    if (isOwnerOrAdmin) {
      list.push(
        { id: 'tenant' as TabId, label: 'Workspace Settings', icon: Building },
        { id: 'team' as TabId, label: 'Team Management', icon: Users }
      );
    }
    return list;
  }, [isOwnerOrAdmin]);

  const handleTabChange = (tabId: TabId) => {
    setSearchParams({ tab: tabId });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-fg-primary">Settings</h1>
        <p className="text-fg-secondary">
          Configure tenant details, fabrication presets, and manage your team.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Navigation Tabs (Sidebar Layout for Settings) */}
        <aside className="w-full md:w-64 shrink-0 flex md:flex-col gap-1 overflow-x-auto md:overflow-x-visible pb-2 md:pb-0">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-3 rounded-md px-4 py-3 text-sm font-semibold whitespace-nowrap transition-all text-left ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-fg-secondary hover:bg-secondary hover:text-fg-primary'
                }`}
              >
                <Icon className="h-4.5 w-4.5 shrink-0" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </aside>

        {/* Tab Content Panels */}
        <main className="flex-grow min-w-0">
          {activeTab === 'profile' && <ProfileSettings />}
          {activeTab === 'tenant' && isOwnerOrAdmin && <TenantSettings />}
          {activeTab === 'team' && isOwnerOrAdmin && <TeamManagement />}
        </main>
      </div>
    </div>
  );
}
